import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { LuLock, LuMail } from 'react-icons/lu';
import AdminAuthShell from '../components/AdminAuthShell';
import { useAdminAuth } from '../context/AdminAuthContext';
import { saveOtpSession } from '../utils/otpSession';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.from?.pathname || '/admin';
  const sessionExpired = new URLSearchParams(location.search).get('expired') === '1';
  const loggedOut = new URLSearchParams(location.search).get('loggedOut') === '1';
  const otpExpired = new URLSearchParams(location.search).get('otpExpired') === '1';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await login(email, password);
      saveOtpSession({
        otpSessionId: data.otpSessionId,
        maskedEmail: data.maskedEmail || email.trim().toLowerCase(),
        expiresInSeconds: data.otpExpiresInSeconds ?? 300,
        redirectTo,
      });
      navigate('/admin/verify-otp', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminAuthShell>
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/15">
          <img src="/img/logo-black.png" alt="RAADSO TECH" className="h-10 w-10 object-contain dark:hidden" />
          <img src="/img/logo-white.png" alt="RAADSO TECH" className="hidden h-10 w-10 object-contain dark:block" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Sign In</h1>
        <div className="mt-3 h-1 w-12 rounded-full bg-primary" />
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-900 dark:text-white">Email</label>
          <div className="relative">
            <LuMail size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-xl border border-primary/25 bg-white py-3.5 pl-12 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-primary/30 dark:bg-[#1a1a1a] dark:text-white dark:placeholder:text-gray-500"
              required
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold text-slate-900 dark:text-white">Password</label>
          <div className="relative">
            <LuLock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full rounded-xl border border-primary/25 bg-white py-3.5 pl-12 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-primary/30 dark:bg-[#1a1a1a] dark:text-white dark:placeholder:text-gray-500"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        {loggedOut ? (
          <p className="rounded-xl border border-sky-300/60 bg-sky-50 px-3 py-2 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200">
            You signed out successfully. Sign in again to continue.
          </p>
        ) : null}

        {sessionExpired ? (
          <p className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            Your session expired. Please sign in again.
          </p>
        ) : null}

        {otpExpired ? (
          <p className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            Your verification code expired. Sign in again to receive a new code.
          </p>
        ) : null}

        {error ? <p className="text-sm text-red-500 dark:text-red-400">{error}</p> : null}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-primary px-6 py-3.5 text-sm font-extrabold text-black shadow-[0_10px_30px_rgba(243,199,21,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-70"
        >
          {loading ? 'Signing in...' : 'Sign In →'}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-slate-500 dark:text-gray-400">
        Back to website?{' '}
        <Link to="/" className="font-bold text-primary hover:underline">
          Visit Home
        </Link>
      </p>
    </AdminAuthShell>
  );
};

export default AdminLogin;
